/**
 * Doc transforms the HTML overlay applies before rendering, plus pure
 * collectors over a parsed Doc. Kept apart from the component so they carry
 * no React or Remotion dependency and can be tested as plain functions.
 */
import type { Doc, Layer } from "@hightouch/lib/ad-studio/html-editor/model";

const AD_REF_IN_URL = /url\(\s*(['"]?)(ad:[^'")]+)\1\s*\)/gi;

/**
 * Swap portable `ad:` references for URLs this render can load. Images carry
 * theirs on `adRef`; anything riding in `extraStyles` (a `url(ad:…)`
 * background) is rewritten in place.
 */
export function resolveDocAssets(
  doc: Doc,
  resolve: (ref: string) => string,
): Doc {
  return {
    ...doc,
    layers: doc.layers.map((layer) => resolveLayerAssets(layer, resolve)),
  };
}

function resolveLayerAssets(
  layer: Layer,
  resolve: (ref: string) => string,
): Layer {
  let next = layer;

  if (next.extraStyles) {
    let changed = false;
    const styles: Record<string, string> = {};
    for (const [prop, value] of Object.entries(next.extraStyles)) {
      const rewritten = value.replace(
        AD_REF_IN_URL,
        (_match, quote: string, ref: string) =>
          `url(${quote}${resolve(ref)}${quote})`,
      );
      if (rewritten !== value) changed = true;
      styles[prop] = rewritten;
    }
    if (changed) next = { ...next, extraStyles: styles };
  }

  if (next.type === "image") {
    const ref = next.adRef ?? (next.src.startsWith("ad:") ? next.src : null);
    if (ref) next = { ...next, src: resolve(ref) };
  }

  if (next.type === "group") {
    next = {
      ...next,
      children: next.children.map((child) =>
        resolveLayerAssets(child, resolve),
      ),
    };
  }

  return next;
}

/**
 * Drop excluded layers from the tree. A group whose children are all excluded
 * is kept — it may still paint (background, border), and dropping it would
 * change the geometry of anything laid out inside it.
 */
export function withoutLayers(doc: Doc, exclude: string[] | undefined): Doc {
  if (!exclude?.length) return doc;
  const excluded = new Set(exclude);
  const keep = (layer: Layer): Layer | null => {
    if (excluded.has(layer.id)) return null;
    if (layer.type !== "group") return layer;
    return {
      ...layer,
      children: layer.children
        .map(keep)
        .filter((child): child is Layer => child !== null),
    };
  };
  return {
    ...doc,
    layers: doc.layers.map(keep).filter((l): l is Layer => l !== null),
  };
}

/** Every image-layer src in the Doc, depth-first. */
export function collectDocImageSources(doc: Doc): string[] {
  const out: string[] = [];
  const walk = (layers: readonly Layer[]): void => {
    for (const layer of layers) {
      if (layer.type === "image") out.push(layer.src);
      if (layer.type === "group") walk(layer.children);
    }
  };
  walk(doc.layers);
  return out;
}

/** Every distinct text-layer font family in the Doc, depth-first. */
export function collectDocFontFamilies(doc: Doc): Set<string> {
  const out = new Set<string>();
  const walk = (layers: readonly Layer[]): void => {
    for (const layer of layers) {
      if (layer.type === "text" && layer.fontFamily) {
        out.add(stripQuotes(layer.fontFamily.trim()));
      }
      if (layer.type === "group") walk(layer.children);
    }
  };
  walk(doc.layers);
  return out;
}

export function stripQuotes(s: string): string {
  return s.replace(/^['"]|['"]$/g, "");
}
